import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import {
  getGuidanceService,
  reserveGuidanceBooking,
  confirmGuidanceBookingPayment,
  requestGuidanceTime,
} from '../services/guidanceBookingService';
import {
  TIMEZONE_OPTIONS,
  detectTimezone,
  computeThreeOptions,
  formatOptionLabel,
  blockStartToDisplayedTime,
} from '../utils/guidanceScheduling';
import { enablePushNotifications } from '../utils/pushNotifications';

const DAY_OPTIONS = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

// Customers call Zee at their booked time (WhatsApp or a normal call). Same number is in the
// confirmation email — backend/src/services/guidance-booking.service.ts (ZEE_PHONE_*).
const ZEE_PHONE_DISPLAY = '+61 466 047 024';
const ZEE_PHONE_TEL = '+61466047024';
const ZEE_WHATSAPP_LINK = 'https://wa.me/61466047024';
const ZEE_WHATSAPP_QR = '/zee-whatsapp-qr.png';

/** How to reach Zee — shown once they're booked (screen + email both carry it). */
function CallZeeCard({ timeLabel }) {
  return (
    <div style={{ ...cardStyle, textAlign: 'center' }}>
      <h3 style={{ color: '#10b981', marginTop: 0, marginBottom: '8px' }}>📲 You call Zee</h3>
      <p style={{ color: '#d1d5db', lineHeight: 1.6, marginBottom: '16px' }}>
        At <strong>{timeLabel}</strong>, call Zee on WhatsApp or a normal phone call. Tap below to say hi on WhatsApp now so you're ready.
      </p>
      <img
        src={ZEE_WHATSAPP_QR}
        alt="Zee's WhatsApp QR code"
        style={{ width: '200px', maxWidth: '70%', background: 'white', borderRadius: '8px', padding: '8px' }}
      />
      <p style={{ color: '#a78bfa', fontSize: '0.85rem', margin: '8px 0 16px' }}>Scan with WhatsApp to add Zee</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
        <a
          href={`${ZEE_WHATSAPP_LINK}?text=${encodeURIComponent(`Hi Zee, I'm booked for my guidance call on ${timeLabel} 😊`)}`}
          target="_blank" rel="noopener noreferrer" style={{ ...btnStyle, textDecoration: 'none', display: 'inline-block', width: 'auto', padding: '12px 24px' }}>
          Add Zee on WhatsApp
        </a>
        <a href={`tel:${ZEE_PHONE_TEL}`} style={{ color: '#4ade80', fontWeight: 600, fontSize: '1.1rem' }}>
          {ZEE_PHONE_DISPLAY}
        </a>
      </div>
    </div>
  );
}

const STRIPE_PUBLISHABLE_KEY_FALLBACK = (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();

async function fetchStripePublishableKey() {
  try {
    const businessId = process.env.REACT_APP_BUSINESS_ID || '';
    const url = `${API_BASE_URL}/business/stripe-public${businessId ? `?businessId=${encodeURIComponent(businessId)}` : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.publishableKey || '').trim();
  } catch (err) {
    console.warn('Could not fetch Stripe key from integrations API, using env fallback:', err.message);
    return STRIPE_PUBLISHABLE_KEY_FALLBACK;
  }
}

const pageStyle = {
  minHeight: '100vh',
  position: 'relative',
  background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
  color: 'white',
};

const containerStyle = {
  padding: '20px',
  width: '100%',
  boxSizing: 'border-box',
  maxWidth: '640px',
  margin: '0 auto',
};

const cardStyle = {
  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.5), rgba(236, 72, 153, 0.5))',
  backdropFilter: 'blur(4px)',
  borderRadius: '12px',
  padding: '32px',
  border: '2px solid #10b981',
  marginBottom: '24px',
};

const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: '8px',
  border: '2px solid rgba(16, 185, 129, 0.5)',
  background: 'rgba(0, 0, 0, 0.4)',
  color: 'white',
  fontSize: '16px',
  boxSizing: 'border-box',
};

const labelStyle = { display: 'block', marginBottom: '8px', color: '#4ade80', fontWeight: 600 };

const btnStyle = {
  background: 'linear-gradient(135deg, #10b981, #059669)',
  color: 'white',
  padding: '14px 28px',
  borderRadius: '8px',
  border: 'none',
  fontSize: '1.1rem',
  fontWeight: 700,
  cursor: 'pointer',
  width: '100%',
};

function AmountStep({ service, onConfirm, submitting, error }) {
  const minAmount = Number(service.price);
  const presets = [minAmount, minAmount + 25, minAmount + 50, minAmount + 100];
  const [selected, setSelected] = useState(minAmount);
  const [custom, setCustom] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  const effectiveAmount = useCustom ? parseFloat(custom) : selected;
  const isValid = Number.isFinite(effectiveAmount) && effectiveAmount >= minAmount;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onConfirm(Math.round(effectiveAmount * 100) / 100);
  };

  const chipStyle = (active) => ({
    padding: '10px 18px',
    borderRadius: '999px',
    border: `2px solid ${active ? '#10b981' : 'rgba(16, 185, 129, 0.5)'}`,
    background: active ? 'rgba(16, 185, 129, 0.3)' : 'rgba(0, 0, 0, 0.4)',
    color: 'white',
    cursor: 'pointer',
    fontWeight: 600,
  });

  return (
    <form onSubmit={handleSubmit}>
      <div style={cardStyle}>
        <h3 style={{ color: '#10b981', marginBottom: '8px' }}>{service.name}</h3>
        <p style={{ color: '#d1d5db', marginBottom: '4px' }}>{service.duration} minutes, one on one.</p>
        <p style={{ color: '#a78bfa', fontSize: '0.95rem', lineHeight: 1.6, margin: '16px 0' }}>
          I give you the ability to assign a value to our conversation. ${minAmount.toFixed(0)} is the minimum — pay what it's worth to you.
        </p>

        <label style={labelStyle}>Choose an amount</label>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {presets.map((p) => (
            <button key={p} type="button" onClick={() => { setSelected(p); setUseCustom(false); }} style={chipStyle(!useCustom && selected === p)}>
              ${p.toFixed(0)}
            </button>
          ))}
          <button type="button" onClick={() => setUseCustom(true)} style={chipStyle(useCustom)}>
            Custom
          </button>
        </div>

        {useCustom && (
          <div style={{ marginBottom: '8px' }}>
            <label style={labelStyle} htmlFor="g-custom-amount">Your amount (min ${minAmount.toFixed(0)})</label>
            <input
              id="g-custom-amount"
              type="number"
              min={minAmount}
              step="1"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder={`${minAmount.toFixed(0)}`}
              style={inputStyle}
            />
          </div>
        )}

        {useCustom && custom !== '' && !isValid && (
          <p style={{ color: '#fca5a5', marginTop: '4px' }}>Minimum is ${minAmount.toFixed(0)}.</p>
        )}
        {error && <p style={{ color: '#fca5a5', marginTop: '12px' }}>{error}</p>}
      </div>
      <button type="submit" disabled={!isValid || submitting} style={btnStyle}>
        {submitting ? 'Setting up payment…' : `Continue with $${Number.isFinite(effectiveAmount) ? effectiveAmount.toFixed(0) : minAmount.toFixed(0)}`}
      </button>
    </form>
  );
}

/** Final step — everything else is already saved, so paying is the last thing they do. */
function PaymentStep({ service, reservation, onPaid, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);
    const result = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });
    if (result.error) {
      setError(result.error.message);
      setProcessing(false);
      return;
    }
    const status = result.paymentIntent?.status;
    if (status !== 'succeeded' && status !== 'processing') {
      setError('Payment was not successful. Please try again.');
      setProcessing(false);
      return;
    }
    // Paid — they're booked in. The server also confirms it from Stripe's webhook, so a failed
    // confirm call here (dropped connection etc.) doesn't lose the booking.
    try {
      await confirmGuidanceBookingPayment({
        bookingId: reservation.bookingId,
        stripePaymentIntentId: reservation.paymentIntentId,
      });
    } catch (err) {
      console.warn('Guidance booking confirm call failed (webhook will confirm):', err);
    }
    onPaid({ processing: status === 'processing' });
  };

  return (
    <form onSubmit={handlePay}>
      <div style={cardStyle}>
        <h3 style={{ color: '#10b981', marginBottom: '8px' }}>Last step — payment</h3>
        <div style={{ color: '#d1d5db', marginBottom: '16px', lineHeight: 1.6 }}>
          <div>{service.name} • {service.duration} min</div>
          <div><strong>{reservation.timeLabel}</strong></div>
          <div style={{ color: '#4ade80', fontWeight: 600 }}>${Number(reservation.amount).toFixed(2)}</div>
        </div>
        <PaymentElement />
        <p style={{ color: '#a78bfa', fontSize: '0.85rem', marginTop: '12px', marginBottom: 0 }}>
          You're booked in the moment your payment goes through.
        </p>
        {error && <p style={{ color: '#fca5a5', marginTop: '12px' }}>{error}</p>}
      </div>
      <button type="submit" disabled={!stripe || processing} style={btnStyle}>
        {processing ? 'Processing…' : `Pay $${Number(reservation.amount).toFixed(2)} & book`}
      </button>
      {!processing && (
        <p style={{ textAlign: 'center', marginTop: '16px' }}>
          <button type="button" onClick={onBack} style={linkBtnStyle}>
            ← Change time or details
          </button>
        </p>
      )}
    </form>
  );
}

const linkBtnStyle = { background: 'none', border: 'none', color: '#22d3ee', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' };

function PreferencesStep({ service, amount, draft, previousBookingId, onReserved, onBack }) {
  const { customer, isAuthenticated, getAuthHeaders } = useCustomerAuth();
  const [timezone, setTimezone] = useState(draft?.timezone || detectTimezone);
  const [windowStart, setWindowStart] = useState(draft?.windowStart || '09:00');
  const [windowEnd, setWindowEnd] = useState(draft?.windowEnd || '17:00');
  const [revealed, setRevealed] = useState(!!draft);
  const [selectedKey, setSelectedKey] = useState(draft?.selectedKey || null);
  const [form, setForm] = useState(draft?.form || { customerName: '', customerEmail: '', customerPhone: '', notes: '' });
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill from the logged-in account, if any — once, so it doesn't clobber their edits.
  useEffect(() => {
    if (isAuthenticated && customer) {
      const name = [customer.firstName, customer.lastName].filter(Boolean).join(' ').trim();
      setForm((f) => ({
        ...f,
        customerName: f.customerName || name || '',
        customerEmail: f.customerEmail || customer.email || '',
        customerPhone: f.customerPhone || customer.phone || '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, customer]);

  const detectedTz = detectTimezone();
  const timezoneChoices = TIMEZONE_OPTIONS.some((t) => t.value === detectedTz)
    ? TIMEZONE_OPTIONS
    : [{ value: detectedTz, label: `Detected: ${detectedTz}` }, ...TIMEZONE_OPTIONS];

  const parseHour = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    return h + (m || 0) / 60;
  };

  const options = React.useMemo(() => {
    const startHour = parseHour(windowStart);
    const endHour = parseHour(windowEnd);
    const computed = computeThreeOptions({
      customerTz: timezone,
      startHour,
      endHour,
      durationMinutes: service.duration,
      availableDays: service.availableDays,
      availableHours: service.availableHours,
    });
    if (!computed) return null;
    const all = [
      { key: 'soonest', title: 'Soonest available', date: blockStartToDisplayedTime(computed.soonest) },
      { key: 'inWindow', title: 'Soonest in your preferred time', date: blockStartToDisplayedTime(computed.inWindow) },
      { key: 'laterInWindow', title: 'A bit further out, in your preferred time', date: blockStartToDisplayedTime(computed.laterInWindow) },
    ];
    // The soonest slot is often also the soonest in their window — don't offer the same time twice.
    return all.filter((opt, i) => all.findIndex((o) => o.date.getTime() === opt.date.getTime()) === i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezone, windowStart, windowEnd, service.duration, service.availableDays, service.availableHours]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!options) {
      setError('Could not compute available times. Please adjust your window and try again.');
      return;
    }
    if (!selectedKey) {
      setError('Please pick one of the times.');
      return;
    }
    if (!form.customerName.trim() || !form.customerEmail.trim()) {
      setError('Please enter your name and email.');
      return;
    }
    if (!form.customerPhone.trim()) {
      setError("Please add your phone number so Zee knows it's you when you call.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const preferredTimeWindows = options.map((opt) => ({
        iso: opt.date.toISOString(),
        label: formatOptionLabel(opt.date, timezone),
        selected: opt.key === selectedKey,
      }));
      const selectedOption = options.find((opt) => opt.key === selectedKey);
      const data = await reserveGuidanceBooking({
        serviceId: service.id,
        amount,
        selectedIso: selectedOption.date.toISOString(),
        preferredTimeWindows,
        customerName: form.customerName.trim(),
        customerEmail: form.customerEmail.trim(),
        customerPhone: form.customerPhone.trim(),
        notes: form.notes.trim() || undefined,
        previousBookingId,
        _hp: honeypot,
        authHeaders: isAuthenticated ? getAuthHeaders() : {},
      });
      onReserved(
        { ...data, timeLabel: formatOptionLabel(selectedOption.date, timezone) },
        { timezone, windowStart, windowEnd, selectedKey, form }
      );
    } catch (err) {
      setError(err.message || 'Could not save your booking');
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
        <label htmlFor="guidance-hp">Leave blank</label>
        <input id="guidance-hp" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
      </div>

      <div style={cardStyle}>
        <p style={{ color: '#86efac', marginBottom: '16px' }}>
          Tell us your timezone and when's usually good, then pick the time that works best. At your time, you call Zee — on WhatsApp or a normal call. Payment is the last step.
        </p>
        <label style={labelStyle} htmlFor="g-timezone">Your timezone</label>
        <select
          id="g-timezone"
          value={timezone}
          onChange={(e) => { setTimezone(e.target.value); setSelectedKey(null); }}
          style={{ ...inputStyle, marginBottom: '16px' }}
        >
          {timezoneChoices.map((tz) => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
        </select>

        <label style={labelStyle}>What time of day usually works for you?</label>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <input
            type="time"
            value={windowStart}
            onChange={(e) => { setWindowStart(e.target.value); setSelectedKey(null); }}
            style={inputStyle}
            aria-label="Preferred window start"
          />
          <input
            type="time"
            value={windowEnd}
            onChange={(e) => { setWindowEnd(e.target.value); setSelectedKey(null); }}
            style={inputStyle}
            aria-label="Preferred window end"
          />
        </div>

        {!revealed && (
          <button type="button" onClick={() => setRevealed(true)} style={btnStyle}>
            Show available times
          </button>
        )}

        {revealed && (
          <>
            <label style={labelStyle}>Pick a time</label>
            {!options && <p style={{ color: '#fca5a5' }}>Could not find available times — try adjusting your window.</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {options && options.map((opt) => {
                const isSelected = selectedKey === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSelectedKey(opt.key)}
                    aria-pressed={isSelected}
                    style={{
                      textAlign: 'left',
                      padding: '14px 16px',
                      borderRadius: '8px',
                      border: `2px solid ${isSelected ? '#10b981' : 'rgba(16, 185, 129, 0.5)'}`,
                      background: isSelected ? 'rgba(16, 185, 129, 0.3)' : 'rgba(0, 0, 0, 0.4)',
                      color: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: '0.8rem', color: '#22d3ee', marginBottom: '4px' }}>{opt.title}</div>
                    <div style={{ fontWeight: 600 }}>{formatOptionLabel(opt.date, timezone)}</div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div style={cardStyle}>
        {isAuthenticated && (
          <p style={{ color: '#86efac', fontSize: '0.85rem', marginTop: 0, marginBottom: '16px' }}>
            Signed in — this booking will show up under Bookings in your account, with a notification once it's confirmed.
          </p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={labelStyle} htmlFor="g-name">Name *</label>
            <input id="g-name" type="text" value={form.customerName} onChange={(e) => setForm((f) => ({ ...f, customerName: e.target.value }))} style={inputStyle} required />
          </div>
          <div>
            <label style={labelStyle} htmlFor="g-email">Email *</label>
            <input id="g-email" type="email" value={form.customerEmail} onChange={(e) => setForm((f) => ({ ...f, customerEmail: e.target.value }))} style={inputStyle} required />
          </div>
          <div>
            <label style={labelStyle} htmlFor="g-phone">Your phone *</label>
            <p style={{ color: '#a78bfa', fontSize: '0.85rem', marginTop: '-4px', marginBottom: '8px' }}>
              So Zee knows it's you when you call.
            </p>
            <input id="g-phone" type="tel" value={form.customerPhone} onChange={(e) => setForm((f) => ({ ...f, customerPhone: e.target.value }))} style={inputStyle} required />
          </div>
          <div>
            <label style={labelStyle} htmlFor="g-notes">What would you like to talk about? (optional)</label>
            <p style={{ color: '#a78bfa', fontSize: '0.85rem', marginTop: '-4px', marginBottom: '8px' }}>
              A light description helps Zee prepare, so you get the most out of your conversation.
            </p>
            <textarea id="g-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="e.g. I'd love guidance on..." style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} rows={3} />
          </div>
        </div>
      </div>

      {error && (
        <div style={{ ...cardStyle, borderColor: '#ef4444', background: 'rgba(239, 68, 68, 0.2)' }}>
          <p style={{ color: '#fca5a5', margin: 0 }}>{error}</p>
        </div>
      )}

      <button type="submit" disabled={submitting} style={btnStyle}>
        {submitting ? 'Saving…' : `Continue to payment ($${Number(amount).toFixed(0)})`}
      </button>
      {!submitting && (
        <p style={{ textAlign: 'center', marginTop: '16px' }}>
          <button type="button" onClick={() => onBack({ timezone, windowStart, windowEnd, selectedKey, form })} style={linkBtnStyle}>
            ← Change amount
          </button>
        </p>
      )}
    </form>
  );
}

function RequestTimeForm({ service, onClose }) {
  const { isAuthenticated, getAuthHeaders } = useCustomerAuth();
  const [timezone, setTimezone] = useState(detectTimezone);
  const [days, setDays] = useState([]);
  const [windowStart, setWindowStart] = useState('18:00');
  const [windowEnd, setWindowEnd] = useState('21:00');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [pushWarning, setPushWarning] = useState(null);

  const detectedTz = detectTimezone();
  const timezoneChoices = TIMEZONE_OPTIONS.some((t) => t.value === detectedTz)
    ? TIMEZONE_OPTIONS
    : [{ value: detectedTz, label: `Detected: ${detectedTz}` }, ...TIMEZONE_OPTIONS];

  const toggleDay = (d) => {
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      setError('Please sign in first so we can notify you once a matching time opens up.');
      return;
    }
    if (days.length === 0) {
      setError('Please pick at least one day.');
      return;
    }
    const startHour = Number(windowStart.split(':')[0]);
    const endHour = Number(windowEnd.split(':')[0]);
    if (startHour >= endHour) {
      setError('Your window end must be after your window start.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await requestGuidanceTime({
        serviceId: service.id,
        timezone,
        preferredDays: days,
        windowStart: startHour,
        windowEnd: endHour,
        notes: notes.trim() || undefined,
        authHeaders: getAuthHeaders(),
      });
      try {
        await enablePushNotifications(getAuthHeaders());
      } catch (pushErr) {
        setPushWarning(pushErr.message || 'Could not enable notifications — check your browser settings so we can reach you.');
      }
      setDone(true);
    } catch (err) {
      setError(err.message || 'Could not submit your request');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div style={cardStyle}>
        <h3 style={{ color: '#10b981', marginBottom: '8px' }}>Request received</h3>
        <p style={{ color: '#d1d5db', lineHeight: 1.6 }}>
          We'll notify you the moment a matching time opens up — then you can come book and pay for your slot.
        </p>
        {pushWarning && <p style={{ color: '#fca5a5', marginTop: '12px' }}>{pushWarning}</p>}
        <button type="button" onClick={onClose} style={{ ...btnStyle, marginTop: '16px' }}>Back</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={cardStyle}>
        <h3 style={{ color: '#10b981', marginBottom: '8px' }}>Request a time</h3>
        <p style={{ color: '#a78bfa', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '16px' }}>
          Don't see a day or time that works? Tell us your days and window — we'll notify you the moment we open up a slot that fits. No payment needed yet.
        </p>

        {!isAuthenticated && (
          <p style={{ color: '#fca5a5', marginBottom: '16px' }}>
            Please <Link to="/auth" style={{ color: '#4ade80' }}>sign in or create an account</Link> first so we can notify you.
          </p>
        )}

        <label style={labelStyle} htmlFor="rt-timezone">Your timezone</label>
        <select id="rt-timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)} style={{ ...inputStyle, marginBottom: '16px' }}>
          {timezoneChoices.map((tz) => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
        </select>

        <label style={labelStyle}>Which days work?</label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {DAY_OPTIONS.map((d) => {
            const active = days.includes(d.value);
            return (
              <button
                key={d.value}
                type="button"
                onClick={() => toggleDay(d.value)}
                style={{
                  padding: '8px 14px',
                  borderRadius: '999px',
                  border: `2px solid ${active ? '#10b981' : 'rgba(16, 185, 129, 0.5)'}`,
                  background: active ? 'rgba(16, 185, 129, 0.3)' : 'rgba(0, 0, 0, 0.4)',
                  color: 'white',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {d.label}
              </button>
            );
          })}
        </div>

        <label style={labelStyle}>What time of day?</label>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <input type="time" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} style={inputStyle} aria-label="Window start" />
          <input type="time" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} style={inputStyle} aria-label="Window end" />
        </div>

        <label style={labelStyle} htmlFor="rt-notes">Anything else? (optional)</label>
        <textarea id="rt-notes" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }} rows={3} />

        {error && <p style={{ color: '#fca5a5', marginTop: '12px' }}>{error}</p>}
      </div>
      <div style={{ display: 'flex', gap: '12px' }}>
        <button type="button" onClick={onClose} style={{ ...btnStyle, flex: 1, background: 'rgba(255, 255, 255, 0.1)' }}>
          Cancel
        </button>
        <button type="submit" disabled={submitting} style={{ ...btnStyle, flex: 1 }}>
          {submitting ? 'Sending…' : 'Request this time'}
        </button>
      </div>
    </form>
  );
}

const GuidanceConversation = () => {
  const [service, setService] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [amount, setAmount] = useState(null);
  // Time + details the customer entered — kept so going back from payment doesn't wipe them.
  const [draft, setDraft] = useState(null);
  // Saved (unpaid) booking + PaymentIntent for the final payment step.
  const [reservation, setReservation] = useState(null);
  // An earlier unpaid reservation from this visit — retired server-side if they reserve again.
  const [previousBookingId, setPreviousBookingId] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [svc, key] = await Promise.all([getGuidanceService(), fetchStripePublishableKey()]);
        if (cancelled) return;
        if (!key) throw new Error('Payment is not configured right now. Please contact us directly.');
        setService(svc);
        setStripePromise(loadStripe(key));
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Failed to load booking page');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (success) {
    return (
      <div style={pageStyle}>
        <div style={containerStyle}>
          <div style={{ ...cardStyle, textAlign: 'center', marginTop: '40px' }}>
            <h2 style={{ color: '#10b981', marginBottom: '16px' }}>
              {success.processing ? 'Payment processing…' : "You're booked in!"}
            </h2>
            <p style={{ color: '#d1d5db', lineHeight: 1.6, marginBottom: '16px' }}>
              {success.processing
                ? <>Your time (<strong>{success.timeLabel}</strong>) is held. We'll confirm by email as soon as your payment clears.</>
                : <>Your call is on <strong>{success.timeLabel}</strong>. A confirmation email with the calendar invite and Zee's details is on its way.</>}
            </p>
            {success.bookingNumber && (
              <p style={{ color: '#4ade80', fontWeight: 600 }}>Reference: {success.bookingNumber}</p>
            )}
          </div>
          <CallZeeCard timeLabel={success.timeLabel} />
          <div style={{ textAlign: 'center' }}>
            <Link to="/" style={{ ...btnStyle, textDecoration: 'none', display: 'inline-block', width: 'auto', padding: '12px 24px', marginTop: '16px' }}>
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <header style={{ textAlign: 'center', marginBottom: '32px', paddingTop: '20px' }}>
          <h1 style={{ fontSize: 'clamp(1.75rem, 5vw, 2.5rem)', fontWeight: 'bold', marginBottom: '12px', color: '#10b981' }}>
            15-Minute Guidance Conversation
          </h1>
          <p style={{ color: '#22d3ee' }}>Pick your time, pay to lock it in, then call Zee at your time 📲</p>
        </header>

        {loadError && (
          <div style={{ ...cardStyle, borderColor: '#ef4444', background: 'rgba(239, 68, 68, 0.2)' }}>
            <p style={{ color: '#fca5a5', margin: 0 }}>{loadError}</p>
          </div>
        )}

        {!loadError && !service && <p style={{ color: '#22d3ee', textAlign: 'center' }}>Loading…</p>}

        {!loadError && service && amount === null && !showRequestForm && (
          <>
            <AmountStep service={service} onConfirm={setAmount} submitting={false} error={null} />
            <p style={{ textAlign: 'center', marginTop: '16px' }}>
              <button type="button" onClick={() => setShowRequestForm(true)} style={linkBtnStyle}>
                Need a different day or time? Request one
              </button>
            </p>
          </>
        )}

        {!loadError && service && showRequestForm && (
          <RequestTimeForm service={service} onClose={() => setShowRequestForm(false)} />
        )}

        {!loadError && service && amount !== null && !reservation && (
          <PreferencesStep
            service={service}
            amount={amount}
            draft={draft}
            previousBookingId={previousBookingId}
            onReserved={(res, enteredDraft) => {
              setDraft(enteredDraft);
              setReservation(res);
            }}
            onBack={(enteredDraft) => {
              setDraft(enteredDraft);
              setAmount(null);
            }}
          />
        )}

        {!loadError && service && reservation && stripePromise && (
          <Elements key={reservation.clientSecret} stripe={stripePromise} options={{ clientSecret: reservation.clientSecret }}>
            <PaymentStep
              service={service}
              reservation={reservation}
              onPaid={({ processing }) =>
                setSuccess({ timeLabel: reservation.timeLabel, bookingNumber: reservation.bookingNumber, processing })
              }
              onBack={() => {
                setPreviousBookingId(reservation.bookingId);
                setReservation(null);
              }}
            />
          </Elements>
        )}
      </div>
    </div>
  );
};

export default GuidanceConversation;
